import type {
  Activity,
  ActivityLogEntry,
  Announcement,
  Department,
  Manager,
  QuickLink,
  StaffDocument,
  StaffRole,
} from "@amazing-care/types";

/**
 * Seed fixture ported from the prototype. Backs the mock data source so the app
 * runs end to end before the Supabase tables exist. Delete once every feature
 * reads from Supabase.
 */
export type MockCurrentUser = {
  name: string;
  role: StaffRole;
};

export const currentUser: MockCurrentUser = {
  name: "Jordan Lee",
  role: "Staff",
};

// Prototype-only: department(s) the mock manager account is allowed to add documents to.
export const MANAGED_DEPARTMENT_IDS: string[] = ["clinical-ops"];

export const announcements: Announcement[] = [
  {
    id: "ann-1",
    title: "Office closed Monday for Labor Day",
    description:
      "All Amazing Care offices will be closed on Monday, September 1st. Normal hours resume Tuesday.",
    timestamp: "2026-08-12T14:30:00",
  },
  {
    id: "ann-2",
    title: "New expense reporting tool goes live",
    description:
      "Starting next week, all expense reports must be submitted through the new finance portal. Training sessions are available.",
    timestamp: "2026-08-11T09:15:00",
  },
  {
    id: "ann-3",
    title: "Open enrollment starts October 1st",
    description:
      "Benefits open enrollment runs October 1–15. Check the HR department page for guides on plan changes.",
    timestamp: "2026-08-08T11:00:00",
  },
  {
    id: "ann-4",
    title: "Scheduled VPN maintenance this weekend",
    description:
      "IT will be performing VPN maintenance Saturday from 10pm–2am. Remote access may be intermittent during this window.",
    timestamp: "2026-08-06T16:45:00",
  },
];

export const activities: Activity[] = [
  {
    id: "act-1",
    title: "Amazing Care named a Top Workplace for 2026",
    date: "2026-08-10",
    content:
      "We're proud to share that Amazing Care has been recognized as a Top Workplace for 2026, an honor based entirely on feedback from our own employees.\n\nThis recognition reflects the dedication of every team member across our clinical, operations, and support functions. Thank you to everyone who took the time to share feedback and to everyone who makes this such a great place to work.\n\nWe'll be celebrating with a company-wide lunch next month — details to follow soon.",
  },
  {
    id: "act-2",
    title: "Clinical Ops team completes new patient intake rollout",
    date: "2026-08-05",
    content:
      "The Clinical Operations team has successfully rolled out the new streamlined patient intake process across all regional offices.\n\nThe new process cuts average intake time by 30% while improving data accuracy for care plans. Special thanks to the field coordinators who piloted the process and provided feedback over the last quarter.\n\nTraining materials are available on the Clinical Ops department page.",
  },
  {
    id: "act-3",
    title: "Q2 volunteer day: over 120 employees participated",
    date: "2026-07-22",
    content:
      "Our Q2 volunteer day brought together more than 120 employees across four cities to support local community health initiatives.\n\nTeams packed care kits, volunteered at community health fairs, and helped renovate a local senior center. It was a great reminder of the impact we can have outside of our day-to-day work.\n\nOur next volunteer day is planned for Q4 — sign-up details will be shared closer to the date.",
  },
  {
    id: "act-4",
    title: "Business Development signs three new regional partnerships",
    date: "2026-07-14",
    content:
      "The Business Development team has closed three new regional partnerships this quarter, expanding our home health coverage into two new counties.\n\nThese partnerships will help us reach more patients who need in-home care services. Onboarding for the new service areas begins next month, and we'll share more details as staffing plans come together.",
  },
];

export const managers: Manager[] = [
  {
    id: "mgr-1",
    name: "Sarah Chen",
    email: "sarah.chen@amazingcare.com",
    departmentIds: ["it"],
    // Also an HR lead through an Entra group, so the admin screen shows both sources.
    ssoDepartmentIds: ["hr"],
  },
  { id: "mgr-2", name: "Marcus Lee", email: "marcus.lee@amazingcare.com", departmentIds: ["hr"] },
  {
    id: "mgr-3",
    name: "Priya Patel",
    email: "priya.patel@amazingcare.com",
    departmentIds: ["clinical-ops"],
  },
  {
    id: "mgr-4",
    name: "David Kim",
    email: "david.kim@amazingcare.com",
    departmentIds: ["business-development"],
  },
  {
    id: "mgr-5",
    name: "Angela Torres",
    email: "angela.torres@amazingcare.com",
    departmentIds: ["business-development"],
  },
];

export const recentActivityLog: ActivityLogEntry[] = [
  {
    id: "log-1",
    actorName: "Sarah Chen",
    action: "uploaded",
    documentName: "Approved Software List",
    departmentLabel: "IT",
    timestamp: "2026-08-13T09:15:00",
  },
  {
    id: "log-2",
    actorName: "Marcus Lee",
    action: "deleted",
    documentName: "Employee Handbook 2025",
    departmentLabel: "HR",
    timestamp: "2026-08-12T16:40:00",
  },
  {
    id: "log-3",
    actorName: "John Smith",
    action: "uploaded",
    documentName: "VPN Setup Instructions",
    departmentLabel: "IT",
    timestamp: "2026-08-12T11:05:00",
  },
  {
    id: "log-4",
    actorName: "Priya Patel",
    action: "uploaded",
    documentName: "HIPAA Compliance Checklist",
    departmentLabel: "Clinical Ops · IHHS",
    timestamp: "2026-08-11T14:20:00",
  },
  {
    id: "log-5",
    actorName: "David Kim",
    action: "uploaded",
    documentName: "Brand Guidelines",
    departmentLabel: "Business Development · Marketing",
    timestamp: "2026-08-10T10:50:00",
  },
  {
    id: "log-6",
    actorName: "Angela Torres",
    action: "deleted",
    documentName: "Old Vendor Contact List",
    departmentLabel: "Business Development",
    timestamp: "2026-08-08T13:30:00",
  },
];

export const departments: Department[] = [
  {
    id: "it",
    name: "IT",
    description: "Technology support, hardware requests, and security guidance for all staff.",
    icon: "/icons/departments/it.png",
  },
  {
    id: "hr",
    name: "HR",
    description: "Benefits, policies, onboarding, and everything related to your employment.",
    icon: "/icons/departments/hr.png",
  },
  {
    id: "clinical-ops",
    name: "Clinical Ops",
    description: "Standard operating procedures, care documentation, and compliance resources.",
    icon: "/icons/departments/clinical-ops.png",
    subDivisions: [
      { id: "pdn", name: "PDN" },
      { id: "ihhs", name: "IHHS" },
      { id: "visit-nursing", name: "Visit Nursing" },
      { id: "therapy", name: "Therapy" },
    ],
  },
  {
    id: "business-development",
    name: "Business Development",
    description: "Partnership resources, brand assets, and templates for growth and outreach.",
    icon: "/icons/departments/business-development.png",
    subDivisions: [
      { id: "marketing", name: "Marketing" },
      { id: "community-relations", name: "Community Relations" },
      { id: "business-outreach", name: "Business Outreach" },
    ],
  },
];

export const documents: StaffDocument[] = [
  // IT
  {
    id: "doc-it-1",
    departmentId: "it",
    name: "VPN Setup Instructions",
    category: "Security",
    type: "PDF",
    lastUpdated: "2026-07-28T10:00:00",
    content: {
      kind: "text",
      body: "Before you begin, confirm you have your company username and the VPN client version approved for your device (see the Approved Software List). Uninstall any personal VPN software before installing the corporate client to avoid network conflicts.\n\nWindows: download the client from the IT portal, run the installer with administrator privileges, and sign in using your company credentials plus your MFA code. macOS: download the .pkg file, allow the system extension in Privacy & Security settings, then connect using the same credentials.\n\nIf the client fails to connect, check that you're on a stable internet connection, restart the client, and confirm your account isn't locked. If the issue persists after two attempts, submit a ticket to the IT helpdesk with your device type and the error message shown.",
    },
  },
  {
    id: "doc-it-2",
    departmentId: "it",
    name: "Password Reset Guide",
    category: "Security",
    type: "PDF",
    lastUpdated: "2026-06-15T10:00:00",
    content: {
      kind: "text",
      body: "Visit the self-service portal at portal.amazingcare.com/reset and enter your company email address. You'll receive a verification code by text or email depending on what you have on file — enter that code to proceed to setting a new password.\n\nYour new password must be at least 12 characters and include a mix of uppercase, lowercase, numbers, and symbols. Passwords cannot reuse any of your last 5 passwords.\n\nIf you're locked out of your account after failed attempts, or you no longer have access to your verification method, contact the IT helpdesk directly — self-service resets are disabled after 5 failed attempts for security reasons.",
    },
  },
  {
    id: "doc-it-3",
    departmentId: "it",
    name: "New Hire Hardware Request Form",
    category: "Hardware",
    type: "DOCX",
    lastUpdated: "2026-05-02T10:00:00",
    content: {
      kind: "text",
      body: "Requesting Manager: ____________________    Department: ____________________\n\nNew Employee Name: ____________________    Start Date: ____________________\n\nEquipment Requested:\n- Laptop (standard / power user — circle one)\n- External monitor(s): ____ (quantity)\n- Keyboard and mouse\n- Headset\n- Docking station\n\nShipping Address (if remote): ____________________\n\nSubmit this completed form to the IT helpdesk queue at least 10 business days before the employee's start date to ensure equipment is ready and configured.",
    },
  },
  {
    id: "doc-it-4",
    departmentId: "it",
    name: "Approved Software List",
    category: "Software",
    type: "XLSX",
    lastUpdated: "2026-08-01T10:00:00",
    content: {
      kind: "sheet",
      columns: ["Software", "Category", "License Type", "Request Process"],
      rows: [
        ["Microsoft 365", "Productivity", "Company-wide", "Pre-installed"],
        ["Slack", "Communication", "Company-wide", "Pre-installed"],
        ["Zoom", "Communication", "Company-wide", "Pre-installed"],
        ["Adobe Acrobat Pro", "Documents", "Per-seat", "Submit IT ticket"],
        ["1Password", "Security", "Per-seat", "Submit IT ticket"],
        ["Figma", "Design", "Per-seat", "Manager approval required"],
        ["Salesforce", "CRM", "Per-seat", "Manager approval required"],
      ],
    },
    featured: true,
  },
  {
    id: "doc-it-5",
    departmentId: "it",
    name: "Reporting a Security Incident",
    category: "Security",
    type: "PDF",
    lastUpdated: "2026-04-20T10:00:00",
    content: {
      kind: "text",
      body: "A security incident includes any suspicious email, unexpected system access, lost or stolen device, or unusual account activity. When in doubt, report it — it's always better to flag something that turns out to be harmless.\n\nTo report an incident, email security@amazingcare.com or call the IT security hotline at extension 4100. Include what happened, when you noticed it, and any device or account involved. Do not forward suspicious emails to coworkers.\n\nIf a device is lost or stolen, report it immediately so it can be remotely locked. The security team will follow up within one business hour for active incidents.",
    },
  },
  // HR
  {
    id: "doc-hr-1",
    departmentId: "hr",
    name: "Employee Benefits Guide",
    category: "Benefits",
    type: "PDF",
    lastUpdated: "2026-08-03T10:00:00",
    content: {
      kind: "text",
      body: "Amazing Care offers medical, dental, and vision coverage to all full-time employees starting the first of the month following 30 days of employment. Three medical plan tiers are available, each with different premium and deductible structures — see the comparison chart during open enrollment.\n\nThe company matches 401(k) contributions up to 4% of salary, with immediate vesting. Employees are automatically enrolled at a 3% contribution rate unless they opt out.\n\nAdditional benefits include an Employee Assistance Program, life insurance, and a wellness stipend. Questions about enrollment or life event changes should be directed to HR at benefits@amazingcare.com.",
    },
    featured: true,
  },
  {
    id: "doc-hr-2",
    departmentId: "hr",
    name: "PTO and Leave Policy",
    category: "Policies",
    type: "PDF",
    lastUpdated: "2026-03-11T10:00:00",
    content: {
      kind: "text",
      body: "Full-time employees accrue PTO at a rate based on tenure: 15 days per year in years 1–3, 20 days in years 4–7, and 25 days for 8+ years. PTO accrues monthly and unused balances roll over up to a maximum of 40 hours.\n\nTo request time off, submit a request through the HR portal at least two weeks in advance for planned absences. Sick leave for illness of three or more consecutive days requires a note from a healthcare provider.\n\nParental leave provides 12 weeks of paid leave for the birth or adoption of a child. Contact HR to begin the leave process as soon as you know your expected date.",
    },
  },
  {
    id: "doc-hr-3",
    departmentId: "hr",
    name: "New Employee Onboarding Checklist",
    category: "Onboarding",
    type: "DOCX",
    lastUpdated: "2026-07-09T10:00:00",
    content: {
      kind: "text",
      body: "Before Day 1:\n- Complete I-9 and W-4 forms in the HR portal\n- Set up direct deposit\n- Review and sign the employee handbook acknowledgment\n\nDay 1:\n- Pick up equipment from IT (or confirm shipment for remote hires)\n- Complete security and compliance training modules\n- Meet with your manager to review 30/60/90 day goals\n\nFirst Week:\n- Schedule 1:1s with core team members\n- Enroll in benefits (30-day window)\n- Review department-specific SOPs relevant to your role",
    },
  },
  {
    id: "doc-hr-4",
    departmentId: "hr",
    name: "Code of Conduct",
    category: "Policies",
    type: "PDF",
    lastUpdated: "2026-01-18T10:00:00",
    content: {
      kind: "text",
      body: "Every employee is expected to treat colleagues, patients, and partners with respect and professionalism. Discrimination, harassment, or retaliation of any kind will not be tolerated and should be reported immediately.\n\nEmployees must avoid conflicts of interest, protect confidential patient and company information, and comply with all applicable healthcare regulations in the course of their work.\n\nConcerns can be reported to your manager, HR, or anonymously through the ethics hotline at 1-800-555-0199. All reports are taken seriously and investigated promptly, and retaliation against anyone who reports in good faith is strictly prohibited.",
    },
  },
  // Clinical Ops — IHHS
  {
    id: "doc-co-1",
    departmentId: "clinical-ops",
    subDivisionId: "ihhs",
    name: "Patient Intake SOP",
    category: "SOPs",
    type: "PDF",
    lastUpdated: "2026-08-05T10:00:00",
    content: {
      kind: "text",
      body: "Upon first contact with a new patient or referral, intake coordinators must verify insurance eligibility and collect demographic information using the standard intake form in the care management system.\n\nA clinical assessment must be scheduled within 48 hours of intake completion. All required consent forms — including HIPAA authorization and treatment consent — must be signed before the first care visit.\n\nIncomplete intake records should be flagged in the system and escalated to the intake supervisor if not resolved within 3 business days.",
    },
  },
  {
    id: "doc-co-3",
    departmentId: "clinical-ops",
    subDivisionId: "ihhs",
    name: "HIPAA Compliance Checklist",
    category: "Compliance",
    type: "PDF",
    lastUpdated: "2026-05-22T10:00:00",
    content: {
      kind: "text",
      body: "Before discussing any patient information, confirm you are speaking with an authorized party — check the patient's file for an approved contact list.\n\nNever discuss patient details in public areas, over unsecured messaging apps, or on personal devices. All patient records must be accessed only through approved, encrypted systems.\n\nWhen sharing records with outside providers, use the secure fax or encrypted portal — never email PHI directly. Report any suspected breach to the compliance officer within 24 hours.",
    },
  },
  {
    id: "doc-co-5",
    departmentId: "clinical-ops",
    subDivisionId: "ihhs",
    name: "IHHS Eligibility Assessment Form",
    category: "Forms",
    type: "DOCX",
    lastUpdated: "2026-03-30T10:00:00",
    content: {
      kind: "text",
      body: "Client Name: ____________________    Date of Assessment: ____________________\n\nAssessor Name: ____________________\n\nFunctional Assessment:\n- Activities of Daily Living (ADLs) needing assistance: ____________________\n- Mobility limitations: ____________________\n- Cognitive status: ____________________\n\nEligibility Determination: Eligible / Not Eligible / Pending Additional Documentation\n\nRecommended Service Hours per Week: ____________________\n\nAssessor Signature: ____________________    Date: ____________________",
    },
  },
  // Clinical Ops — PDN
  {
    id: "doc-co-4",
    departmentId: "clinical-ops",
    subDivisionId: "pdn",
    name: "Medication Administration SOP",
    category: "SOPs",
    type: "PDF",
    lastUpdated: "2026-02-14T10:00:00",
    content: {
      kind: "text",
      body: "Before administering any medication, verify the order against the current medication administration record (MAR) and confirm the patient's identity using two identifiers.\n\nDocument the medication, dose, route, and time immediately after administration — never in advance. Any refusal, adverse reaction, or missed dose must be documented and reported to the supervising nurse the same day.\n\nControlled substances require dual sign-off and must be logged in the controlled substance record at both administration and any waste.",
    },
  },
  {
    id: "doc-co-6",
    departmentId: "clinical-ops",
    subDivisionId: "pdn",
    name: "PDN Shift Documentation Guide",
    category: "Documentation",
    type: "PDF",
    lastUpdated: "2026-07-11T10:00:00",
    content: {
      kind: "text",
      body: "Shift notes must be completed before the end of each shift and include vital signs, care provided, medications administered, and any changes in patient condition.\n\nUse objective, factual language — describe what was observed and done rather than assumptions about cause. Any significant change in condition must also be reported by phone to the case manager per the escalation policy.\n\nIncomplete or late shift notes may delay billing and should be flagged to your supervisor if you're unable to complete documentation on time.",
    },
  },
  // Clinical Ops — Visit Nursing
  {
    id: "doc-co-2",
    departmentId: "clinical-ops",
    subDivisionId: "visit-nursing",
    name: "Care Plan Template",
    category: "Templates",
    type: "DOCX",
    lastUpdated: "2026-06-30T10:00:00",
    content: {
      kind: "text",
      body: "Patient Name: ____________________    Date of Plan: ____________________\n\nDiagnosis / Primary Concerns: ____________________\n\nGoals of Care:\n1. ____________________\n2. ____________________\n3. ____________________\n\nInterventions and Frequency: ____________________\n\nCare Team Members: ____________________\n\nPlan Review Date: ____________________",
    },
  },
  {
    id: "doc-co-8",
    departmentId: "clinical-ops",
    subDivisionId: "visit-nursing",
    name: "Home Visit Checklist",
    category: "SOPs",
    type: "PDF",
    lastUpdated: "2026-04-16T10:00:00",
    content: {
      kind: "text",
      body: "Before the Visit: confirm the appointment with the patient or caregiver, review the most recent care notes, and pack required supplies and equipment.\n\nDuring the Visit: verify patient identity, complete the planned assessment or treatment, document vitals and observations in real time where possible, and address any caregiver questions or concerns.\n\nAfter the Visit: complete visit documentation before leaving the location when possible, schedule the next visit if needed, and flag any urgent concerns to the care team immediately.",
    },
  },
  // Clinical Ops — Therapy
  {
    id: "doc-co-7",
    departmentId: "clinical-ops",
    subDivisionId: "therapy",
    name: "Therapy Session Documentation Form",
    category: "Documentation",
    type: "DOCX",
    lastUpdated: "2026-05-08T10:00:00",
    content: {
      kind: "text",
      body: "Patient Name: ____________________    Session Date: ____________________\n\nSession Type: ____________________    Duration: ____________________\n\nProgress Toward Goals: ____________________\n\nInterventions Used: ____________________\n\nPatient Response: ____________________\n\nPlan for Next Session: ____________________\n\nTherapist Signature: ____________________",
    },
  },
  {
    id: "doc-co-9",
    departmentId: "clinical-ops",
    subDivisionId: "therapy",
    name: "Therapy Plan of Care Template",
    category: "Templates",
    type: "DOCX",
    lastUpdated: "2026-03-21T10:00:00",
    content: {
      kind: "text",
      body: "Patient Name: ____________________    Referring Physician: ____________________\n\nDiagnosis: ____________________\n\nLong-Term Goals: ____________________\n\nShort-Term Goals: ____________________\n\nFrequency and Duration of Treatment: ____________________ sessions per week for ____________________ weeks\n\nTherapist Signature: ____________________    Date: ____________________",
    },
  },
  // Business Development — Marketing
  {
    id: "doc-bd-2",
    departmentId: "business-development",
    subDivisionId: "marketing",
    name: "Brand Guidelines",
    category: "Brand Assets",
    type: "PDF",
    lastUpdated: "2026-04-09T10:00:00",
    content: {
      kind: "text",
      body: "The Amazing Care logo must always appear in full color on light backgrounds or in white on dark or photographic backgrounds. Never stretch, recolor, or add effects to the logo, and maintain clear space equal to the height of the logo mark on all sides.\n\nOur primary color palette consists of deep teal, warm gray, and a soft accent gold — use these consistently across all external materials. Approved fonts are listed in the typography section; substitute system fonts only when the approved fonts are unavailable.\n\nAll external-facing materials — including flyers, presentations, and signage — should be reviewed by the marketing team before distribution to ensure brand consistency.",
    },
  },
  {
    id: "doc-bd-4",
    departmentId: "business-development",
    subDivisionId: "marketing",
    name: "Social Media Style Guide",
    category: "Brand Assets",
    type: "PDF",
    lastUpdated: "2026-06-19T10:00:00",
    content: {
      kind: "text",
      body: "Our voice on social media is warm, professional, and community-focused — we highlight real stories from patients, caregivers, and staff wherever possible with proper consent.\n\nUse sentence case for post copy, keep captions under three short paragraphs, and always include alt text on images for accessibility. Avoid clinical jargon; write as if explaining to a family member.\n\nAll posts referencing specific patients, partnerships, or clinical outcomes must be reviewed by marketing and compliance before publishing.",
    },
  },
  // Business Development — Community Relations
  {
    id: "doc-bd-5",
    departmentId: "business-development",
    subDivisionId: "community-relations",
    name: "Community Event Planning Guide",
    category: "Events",
    type: "DOCX",
    lastUpdated: "2026-05-27T10:00:00",
    content: {
      kind: "text",
      body: "8 Weeks Out: confirm venue, date, and budget; identify staffing needs and begin outreach to local partners.\n\n4 Weeks Out: finalize marketing materials, confirm volunteer sign-ups, and order any branded materials or giveaways.\n\n1 Week Out: confirm final headcount, pack supplies, and send day-of logistics to all staff and volunteers.\n\nDay Of: arrive early for setup, designate a point person for questions, and collect attendee feedback where possible.\n\nAfter the Event: send thank-you notes to partners and volunteers, and submit a summary report to the community relations lead.",
    },
  },
  {
    id: "doc-bd-6",
    departmentId: "business-development",
    subDivisionId: "community-relations",
    name: "Volunteer Partnership Agreement",
    category: "Templates",
    type: "DOCX",
    lastUpdated: "2026-02-24T10:00:00",
    content: {
      kind: "text",
      body: 'This agreement is entered into between Amazing Care and ____________________ ("Partner Organization") for the purpose of coordinating volunteer activities in support of community health initiatives.\n\nAmazing Care agrees to provide event coordination support, staff participation where applicable, and promotional support through company channels.\n\nThe Partner Organization agrees to provide volunteer coordination on-site, adhere to Amazing Care\'s community conduct guidelines, and confirm participant headcounts at least one week prior to any joint event.\n\nEither party may terminate this agreement with 30 days written notice.',
    },
  },
  // Business Development — Business Outreach
  {
    id: "doc-bd-1",
    departmentId: "business-development",
    subDivisionId: "business-outreach",
    name: "Partnership Proposal Template",
    category: "Templates",
    type: "PPTX",
    lastUpdated: "2026-07-17T10:00:00",
    content: {
      kind: "slides",
      slides: [
        {
          title: "Partnering with Amazing Care",
          bullets: [
            "In-home health services across [region]",
            "Trusted by [X] referral partners",
            "Presented to: [Organization Name]",
          ],
        },
        {
          title: "Who We Are",
          bullets: [
            "Licensed home health provider since [year]",
            "Services: PDN, IHHS, visit nursing, therapy",
            "[X] clinicians across [X] counties",
          ],
        },
        {
          title: "The Opportunity",
          bullets: [
            "Expanding coverage into [service area]",
            "Addressing gaps in [specific need]",
            "Proposed partnership structure",
          ],
        },
        {
          title: "What We Provide",
          bullets: [
            "Coordinated referral process",
            "Dedicated liaison for your organization",
            "Joint reporting on shared patients",
          ],
        },
        {
          title: "Next Steps",
          bullets: [
            "Schedule a follow-up call",
            "Share referral agreement for review",
            "Target start date: [date]",
          ],
        },
      ],
    },
  },
  {
    id: "doc-bd-3",
    departmentId: "business-development",
    subDivisionId: "business-outreach",
    name: "Regional Market Overview",
    category: "Research",
    type: "XLSX",
    lastUpdated: "2026-06-02T10:00:00",
    content: {
      kind: "sheet",
      columns: ["County", "Population 65+", "Current Coverage", "Competitor Density", "Priority"],
      rows: [
        ["Fairview County", "48,200", "Full", "Medium", "Maintain"],
        ["Lakeside County", "31,500", "Partial", "Low", "High"],
        ["Millbrook County", "22,900", "None", "Low", "High"],
        ["Cedar County", "56,700", "Full", "High", "Maintain"],
        ["Ashford County", "18,300", "None", "Medium", "Medium"],
      ],
    },
  },
];

export function getDepartment(departmentId: string) {
  return departments.find((department) => department.id === departmentId);
}

export function getDocumentsByDepartment(departmentId: string) {
  return documents.filter((document) => document.departmentId === departmentId);
}

export function getDocument(documentId: string) {
  return documents.find((document) => document.id === documentId);
}

export function getActivity(activityId: string) {
  return activities.find((activity) => activity.id === activityId);
}

// Hot buttons as Brandon described them on 20 Aug: one for the season, one
// that stays up until it is removed.
export const quickLinks: QuickLink[] = [
  {
    id: "link-open-enrollment",
    label: "Open enrollment: benefits",
    departmentId: "hr",
    category: "Benefits",
  },
  {
    id: "link-it-security",
    label: "Cybersecurity resources",
    departmentId: "it",
  },
];
